import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, ErrorAlert, Field } from '../components/ui';

type Any = Record<string, any>;

export function NieboGuardImportSection() {
  const [content, setContent] = useState('');
  const [format, setFormat] = useState<'auto' | 'json' | 'markdown'>('auto');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<Any | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const readFile = async (selected: File | null) => {
    if (!selected) return;
    const text = await selected.text();
    setContent(text);
    setFormat(selected.name.toLowerCase().endsWith('.json') ? 'json' : 'markdown');
    if (!title) setTitle(selected.name.replace(/\.(md|markdown|json|txt)$/i, ''));
  };

  const submit = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const response = await api.nieboguard.importMaterial({ content, format, title: title.trim() || undefined, category: category.trim() || undefined, sourceUrl: sourceUrl.trim() || undefined, language: 'pl' });
      setResult(response);
      setContent('');
      if (file.current) file.current.value = '';
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <>
      {error ? <ErrorAlert error={error} /> : null}
      <Card title="Import materiału do researchu" actions={<Badge tone="warning">szkic — wymaga weryfikacji</Badge>}>
        <p className="nb-stat-note">Wklej materiał z ChatGPT lub innego źródła jako Markdown/JSON. System utworzy <strong>tematy szkicowe</strong> w Topic Library, sprawdzi duplikaty i <strong>nie zatwierdzi ich automatycznie</strong>.</p>
        <div className="nb-row" style={{ marginBottom: 10 }}>
          <input ref={file} type="file" accept=".md,.markdown,.json,.txt" className="input" style={{ maxWidth: 340 }} onChange={(event) => void readFile(event.target.files?.[0] ?? null)} />
          <Button size="sm" variant="ghost" onClick={() => file.current?.click()}>Wybierz plik .md / .json</Button>
        </div>
        <div className="nb-grid nb-grid--2">
          <Field label="Tytuł (tylko dla pojedynczego materiału)"><input className="input" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="np. Jak działa ADS-B?" /></Field>
          <Field label="Kategoria"><input className="input" value={category} onChange={(event) => setCategory(event.target.value)} placeholder="np. LOTNICTWO_DRONY" /></Field>
          <Field label="Źródło URL (opcjonalnie)"><input className="input" value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://…" /></Field>
          <Field label="Format"><select className="select" value={format} onChange={(event) => setFormat(event.target.value as typeof format)}><option value="auto">AUTO</option><option value="markdown">Markdown</option><option value="json">JSON</option></select></Field>
        </div>
        <Field label="Treść materiału"><textarea className="input" rows={14} value={content} onChange={(event) => setContent(event.target.value)} placeholder={'# Temat\n\nTreść…\n\n## Drugi temat\n\nTreść…'} style={{ width: '100%', fontFamily: 'monospace' }} /></Field>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Button variant="primary" disabled={busy || content.trim().length < 20} onClick={submit}>{busy ? 'Importuję…' : 'Importuj i sprawdź duplikaty'}</Button>
          <Button variant="ghost" onClick={() => { setContent(''); setResult(null); setTitle(''); setCategory(''); setSourceUrl(''); }}>Wyczyść</Button>
        </div>
        <p className="nb-stat-note">JSON może zawierać tablicę tematów lub obiekt z polami <code>topics</code>/<code>items</code>. Markdown dzieli materiał po nagłówkach <code>##</code>. Każdy wpis trafia jako DRAFT/PENDING — przed produkcją musisz zweryfikować źródło i zatwierdzić temat.</p>
      </Card>

      {result ? <Card title="Wynik importu">
        <div className="nb-item__meta"><Badge tone="success">utworzone: {result.created}</Badge><Badge tone="warning">duplikaty: {result.duplicates}</Badge><span>kandydaci: {result.candidates}</span></div>
        <div className="nb-list" style={{ marginTop: 10 }}>
          {(result.results ?? []).map((item: Any, index: number) => (
            <div className="nb-item" key={`${item.topicId ?? item.duplicateOf}-${index}`}>
              <div className="nb-item__title">{item.title}</div>
              <div className="nb-item__meta"><Badge tone={item.status === 'CREATED' ? 'success' : 'warning'}>{item.status}</Badge>{item.topicId ? <span>topic: {item.topicId}</span> : null}{item.duplicateOf ? <span>duplikat: {item.duplicateOf}</span> : null}</div>
            </div>
          ))}
        </div>
        <p className="nb-stat-note">Przejdź do <strong>Edu Shorts / Topic Library</strong>, zweryfikuj źródło i zatwierdź temat, a potem utwórz pakiet w <strong>Content Packages</strong>.</p>
      </Card> : null}
    </>
  );
}
